import { useAuth } from '@saltus/auth';
import { isEmpty } from 'lodash';
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import getAccountTypeAction from '../actions/accounttype/get';
import getSignature from '../actions/actioncentersignature/get';
import setSignatureForContactAction from '../actions/actioncentersignature/set';
import getApprovalDocsAction from '../actions/approvaldocs/get';
import getContactStatusAction from '../actions/contacts/getContactStatus';
import getContactAction from '../actions/contacts/set';
import getDummyContactAction from '../actions/contacts/setdummy';
import getDashboardAction from '../actions/dashboard/get';
import getDocumentsForContactAction from '../actions/documents/get';
import getDocumentsForAdviserAction from '../actions/documentsadvisers/get';
import getForms from '../actions/form/getForms';
import listNotificationsAction from '../actions/notifications/list';
import getSaltusOwnersForContactAction from '../actions/saltusowners/get';
import getSessionAction from '../actions/session/get';
import Loading from '../components/loading/component';
import isInGroup from '../services/util/isInGroup';
import { MapGetter, State } from '../utils/map';

interface PathConfig {
  clientOnlyRoute: boolean;
  requires?: {
    client: boolean;
    contact: boolean;
    dashboard: boolean;
    documents: boolean;
    session: boolean;
  };
  status: 'complete' | 'loading' | 'passthrough' | 'redirect';
}

interface Contact {
  ContactID: string;
}

interface Dashboard {
  planOwners: {
    Label: string;
    PlanOwnerID: string;
    Plans: {
      PlanID: string;
    }[];
  }[];
}

interface Session {
  identityId: string;
  userSub: string;
  groups?: string[] | null;
}

export interface ProfileContextValues {
  contact: Contact;
  dashboard: Dashboard;
  signature?: string;
  status: string;
  setSignature: (value: string) => void;
}

export const ProfileContext = createContext<ProfileContextValues | null>(null);

interface ProfileProviderProps {
  children: ReactNode;
}

function ProfileProvider({ children }: ProfileProviderProps) {
  const { authStatus } = useAuth();

  const location = useLocation();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [admin, setAdmin] = useState(false);

  const [adviser, setAdviser] = useState(false);

  const [client, setClient] = useState(false);

  const [prospect, setProspect] = useState(false);

  const contact = useSelector<State<Contact>, MapGetter<Contact>>((state) =>
    state.contacts.get('set'),
  ) as Contact; // We guard against this being undefined further below

  const dashboard = useSelector<State<Dashboard>, MapGetter<Dashboard>>(
    (state) => state.dashboard.get('data'),
  ) as Dashboard; // We guard against this being undefined further below

  const [clientDocs, adviserDocs] = useSelector<
    State<boolean>,
    MapGetter<boolean>[]
  >(
    (state) => [
      state.documents.get('gettingdata'),
      state.documentsAdvisers.get('gettingdata'),
    ],
    shallowEqual,
  );

  const signature = useSelector<State<string>, MapGetter<string>>(
    (state) =>
      state.signatureData.getIn(['data', 'Signature']) as string | undefined,
  );

  const setSignature = useCallback(
    (val: string) => {
      dispatch(setSignatureForContactAction(val));
    },
    [dispatch],
  );

  const session = useSelector<State<Session>, MapGetter<Session>>((state) =>
    state.session.get('data'),
  );

  const status = useSelector<State<string>, MapGetter<string>>((state) =>
    state.contacts.get('contactstatus'),
  ) as string; // We guard against this being undefined further below

  const pathConfig = useMemo((): PathConfig => {
    const config = [
      {
        clientOnlyRoute: true,
        path: '/actions',
        requires: {
          client: false,
          contact: true,
          dashboard: false,
          documents: true,
          session: true,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/admin',
        requires: {
          client: false,
          contact: false,
          dashboard: false,
          documents: false,
          session: true,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/adviser',
        requires: {
          client: false,
          contact: false,
          dashboard: false,
          documents: false,
          session: true,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/contact-us',
        requires: {
          client: !prospect,
          contact: false,
          dashboard: false,
          documents: false,
          session: false,
        },
      },
      {
        clientOnlyRoute: true,
        path: '/dashboard',
        requires: {
          client,
          contact: true,
          dashboard: true,
          documents: false,
          session: true,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/documents',
        requires: {
          client: false,
          contact: client ? !prospect : false,
          dashboard: false,
          documents: true,
          session: true,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/form',
        requires: {
          client: false,
          contact: true,
          dashboard: true,
          documents: false,
          session: false,
        },
      },
      {
        clientOnlyRoute: true,
        path: '/personal-details',
        requires: {
          client: true,
          contact: true,
          dashboard: true,
          documents: false,
          session: false,
        },
      },
      {
        clientOnlyRoute: false,
        path: '/security',
        requires: {
          client: false,
          contact: false,
          dashboard: false,
          documents: false,
          session: true,
        },
      },
    ];

    const path = config.find((item) => location.pathname.startsWith(item.path));

    if (!path) {
      return {
        clientOnlyRoute: false,
        status: 'passthrough',
      };
    }

    // The prospect redirect only applies to prospects signed in themselves.
    // Advisers and admins viewing a record (e.g. a shell client) must be able
    // to open client-only routes such as `/personal-details`. See SWP-1618.
    if (path.requires.client && prospect && !admin && !adviser) {
      return {
        clientOnlyRoute: path.clientOnlyRoute,
        requires: path.requires,
        status: 'redirect',
      };
    }

    if (path.requires.contact && isEmpty(contact)) {
      return {
        clientOnlyRoute: path.clientOnlyRoute,
        requires: path.requires,
        status: 'loading',
      };
    }

    if (path.requires.dashboard && isEmpty(dashboard)) {
      return {
        clientOnlyRoute: path.clientOnlyRoute,
        requires: path.requires,
        status: 'loading',
      };
    }

    if (path.requires.documents && (clientDocs || adviserDocs)) {
      return {
        clientOnlyRoute: path.clientOnlyRoute,
        requires: path.requires,
        status: 'loading',
      };
    }

    if (path.requires.session && isEmpty(session)) {
      return {
        clientOnlyRoute: path.clientOnlyRoute,
        requires: path.requires,
        status: 'loading',
      };
    }

    return {
      clientOnlyRoute: path.clientOnlyRoute,
      requires: path.requires,
      status: 'complete',
    };
  }, [
    admin,
    adviser,
    adviserDocs,
    client,
    clientDocs,
    contact,
    dashboard,
    location.pathname,
    prospect,
    session,
  ]);

  const value = useMemo(
    () => ({
      contact,
      dashboard,
      setSignature,
      signature,
      status,
    }),
    [contact, dashboard, setSignature, signature, status],
  );

  useEffect(() => {
    // Only a literal 'Prospect' status marks a prospect. Shell clients have
    // no CRM status (the API returns an empty string) but do have contact
    // data, so advisers viewing them must not be redirected away from
    // client-only routes such as `/personal-details`. See SWP-1618.
    const result = status === 'Prospect';

    setProspect(result);
  }, [status]);

  useEffect(() => {
    setAdmin(isInGroup(session, 'Admin'));

    setAdviser(isInGroup(session, 'Adviser'));

    setClient(isInGroup(session, 'Client'));
  }, [session]);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      dispatch(getSessionAction());
    }
  }, [authStatus, dispatch]);

  useEffect(() => {
    if (client && session?.userSub) {
      dispatch(getDummyContactAction());
    }
  }, [client, dispatch, session]);

  useEffect(() => {
    dispatch(getContactStatusAction(contact?.ContactID ?? 0));
  }, [contact, dispatch]);

  useEffect(() => {
    if (pathConfig.requires?.contact) {
      if (!isEmpty(contact)) {
        dispatch(getContactAction());
        dispatch(getDashboardAction(contact.ContactID ?? 0));
        dispatch(getApprovalDocsAction(contact?.ContactID ?? 0));
        dispatch(getForms(contact?.ContactID));
      }

      if (contact?.ContactID) {
        dispatch(listNotificationsAction());
        dispatch(getSignature());
      }
    }
  }, [contact, dispatch, pathConfig.requires?.contact]);

  useEffect(() => {
    if (pathConfig.requires?.documents) {
      dispatch(getDocumentsForContactAction());
    }
  }, [contact, dispatch, pathConfig.requires?.documents]);

  useEffect(() => {
    if (dashboard && session?.userSub) {
      dispatch(getAccountTypeAction());

      if (pathConfig.requires?.contact) {
        dispatch(getSaltusOwnersForContactAction());
      }

      if (adviser) {
        dispatch(getDocumentsForAdviserAction());
      }
    }
  }, [
    adviser,
    dashboard,
    dispatch,
    pathConfig.requires?.contact,
    session?.userSub,
  ]);

  useEffect(() => {
    // Replace (not push) so the client-only route is removed from history.
    // Otherwise the back button pops to it and this effect redirects again,
    // trapping the user. See SWP-1618.
    if (admin && pathConfig.clientOnlyRoute && isEmpty(contact)) {
      navigate('/admin', { replace: true });
    }

    if (adviser && pathConfig.clientOnlyRoute && isEmpty(contact)) {
      navigate('/adviser', { replace: true });
    }
  }, [admin, adviser, contact, navigate, pathConfig.clientOnlyRoute]);

  if (pathConfig.status === 'redirect') {
    // Replace so the guarded route (e.g. `/personal-details` for a shell
    // client) is removed from history. Otherwise the back button pops to it
    // and this guard pushes `/actions` again — trapping the user in a loop.
    // See SWP-1618.
    //
    return <Navigate to="/actions" replace />;
  }

  if (pathConfig.status === 'loading') {
    return <Loading />;
  }

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

export default ProfileProvider;
